/**
 * useDeviceLinking - Hook for device link/unlink/available operations
 * Provides API methods for the unified device linking system.
 */

import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { authenticatedFetch } from '@/lib/api';
import type { DeviceType } from './useSystemDevices';

export type { DeviceType } from './useSystemDevices';

export interface AvailableDevice {
  id: string;
  unit_id: string | null;
  last_seen_at: string | null;
  // Starlink-specific
  external_terminal_id?: string;
  service_line_nickname?: string;
  connection_status?: string;
  is_active?: boolean;
  // Sol-Ark-specific
  external_inverter_id?: string;
  solark_name?: string | null;
  // Omni-specific
  external_device_name?: string;
  // Tesla-specific
  external_charger_id?: string;
  // Shared by Omni (holds the IMEI) and HVAC — separate tables, same column name
  external_device_id?: string;
  // Generic
  last_status?: Record<string, unknown>;
}

export interface LinkedDevices {
  starlink: AvailableDevice | null;
  solark: AvailableDevice | null;
  omni: AvailableDevice | null;
  tesla: AvailableDevice | null;
  hvac: AvailableDevice | null;
}

export interface LinkConflict {
  conflictType: 'device_linked_elsewhere' | 'unit_has_device';
  message: string;
  reassignable: boolean;
  currentUnitId?: string;
  currentUnitCode?: string;
  existingDeviceId?: string;
  existingDeviceExternalId?: string;
}

interface UseDeviceLinkingReturn {
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  availableDevices: AvailableDevice[];
  linkedDevices: LinkedDevices | null;
  fetchAvailable: (type: DeviceType, search?: string) => Promise<void>;
  fetchLinked: (unitId: string) => Promise<void>;
  linkDevice: (unitId: string, type: DeviceType, deviceId: string, force?: boolean, reason?: string) => Promise<{ success: boolean; conflict?: LinkConflict }>;
  unlinkDevice: (unitId: string, type: DeviceType, deviceId?: string) => Promise<boolean>;
}

export function useDeviceLinking(): UseDeviceLinkingReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableDevices, setAvailableDevices] = useState<AvailableDevice[]>([]);
  const [linkedDevices, setLinkedDevices] = useState<LinkedDevices | null>(null);

  const fetchAvailable = useCallback(async (type: DeviceType, search?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search && search.length >= 2) params.set('search', search);
      const response = await authenticatedFetch(`/api/v1/admin/units/devices/${type}/available?${params}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      setAvailableDevices(result.data || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch devices';
      setError(message);
      setAvailableDevices([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchLinked = useCallback(async (unitId: string) => {
    setIsLoading(true);
    try {
      const response = await authenticatedFetch(`/api/v1/admin/units/${unitId}/devices`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setLinkedDevices(data);
    } catch (err) {
      console.error('[useDeviceLinking] fetchLinked error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const linkDevice = useCallback(async (
    unitId: string,
    type: DeviceType,
    deviceId: string,
    force = false,
    // Required by the API whenever force displaces an existing link, so the
    // audit row can answer WHY the previous device was swapped out.
    reason?: string,
  ): Promise<{ success: boolean; conflict?: LinkConflict }> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`/api/v1/admin/units/${unitId}/link-${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId, force, ...(reason ? { reason } : {}) }),
      });

      if (response.status === 409) {
        const conflict = await response.json();
        // Older route responses did not forward conflictType. A populated
        // currentUnitCode uniquely identifies the reassignable "this device is
        // elsewhere" case; target-unit conflicts must remain blocking.
        const conflictType = conflict.conflictType === 'device_linked_elsewhere'
          || conflict.conflictType === 'unit_has_device'
          ? conflict.conflictType
          : conflict.currentUnitCode
            ? 'device_linked_elsewhere'
            : 'unit_has_device';

        return {
          success: false,
          conflict: {
            conflictType,
            message: conflict.message || 'Device could not be linked',
            reassignable: conflictType === 'device_linked_elsewhere',
            currentUnitId: conflict.currentUnitId,
            currentUnitCode: conflict.currentUnitCode,
            existingDeviceId: conflict.existingDeviceId,
            existingDeviceExternalId: conflict.existingDeviceExternalId,
          },
        };
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      toast.success('Device linked successfully');
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to link device';
      setError(message);
      toast.error(message);
      return { success: false };
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  // deviceId is required when the unit has more than one device of this type —
  // a unit may run two Sol-Ark inverters, and the backend refuses to guess which
  // one to detach (it returns an ambiguous_device conflict instead).
  const unlinkDevice = useCallback(async (
    unitId: string,
    type: DeviceType,
    deviceId?: string,
  ): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`/api/v1/admin/units/${unitId}/unlink-${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        ...(deviceId ? { body: JSON.stringify({ deviceId }) } : {}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      toast.success('Device unlinked successfully');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to unlink device';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return {
    isLoading,
    isSubmitting,
    error,
    availableDevices,
    linkedDevices,
    fetchAvailable,
    fetchLinked,
    linkDevice,
    unlinkDevice,
  };
}
